import { downloadFileThunk, uploadFileThunk } from '../../../../../store/general/thunks';
import { documentSlots, UPLOAD_ERROR_MESSAGE } from '../../constants';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import { setNotification } from '../../../../../store/general/slice';
import { useDispatch, useSelector } from '../../../../../store/store';
import { selectUserId } from '../../../../../store/auth/selectors';
import { useProfileUpdate } from '../../hooks/useProfileUpdate';
import { DocumentSlot, ProfilePatch } from '../../types';
import DocumentCard from './DocumentCard';
import styles from './style.module.scss';
import { useState } from 'react';

/** Résumé, portfolio, cover letter and certificate the applicant has on file. */
function UploadedFiles() {
    const dispatch = useDispatch();
    const userId = useSelector(selectUserId);
    const { profile, isSaving, saveProfile } = useProfileUpdate();

    const [uploading, setUploading] = useState(false);
    const [editing, setEditing] = useState<DocumentSlot | null>(null);
    const [deleting, setDeleting] = useState<DocumentSlot | null>(null);

    const uploadDocument = async (slot: DocumentSlot, file: File) => {
        if (!userId) return;
        setUploading(true);

        try {
            await dispatch(uploadFileThunk({ fileName: `${userId}/${file.name}`, file })).unwrap();
        } catch {
            setUploading(false);
            dispatch(setNotification([UPLOAD_ERROR_MESSAGE, 'error']));
            return;
        }

        const saved = await saveProfile({ [slot.field]: file.name } as ProfilePatch);
        setUploading(false);
        if (saved) setEditing(null);
    };

    const deleteDocument = async () => {
        if (!deleting) return;
        const saved = await saveProfile({ [deleting.field]: '' } as ProfilePatch);
        if (saved) setDeleting(null);
    };

    const downloadDocument = (fileName: string) => {
        dispatch(downloadFileThunk({ path: `${userId}/${fileName}`, fileName }));
    };

    return (
        <div className={styles.wrapper}>
            {documentSlots.map(slot => {
                const fileName = profile?.[slot.field] || '';

                return (
                    <DocumentCard
                        slot={slot}
                        key={slot.type}
                        fileName={fileName}
                        uploading={uploading}
                        isEditing={editing?.type === slot.type}
                        onEdit={() => setEditing(slot)}
                        onDelete={() => setDeleting(slot)}
                        onDownload={() => downloadDocument(fileName)}
                        onUpload={file => uploadDocument(slot, file)}
                    />
                );
            })}

            {deleting && <ConfirmDeleteModal
                loading={isSaving}
                title='Delete File'
                onConfirm={deleteDocument}
                onCancel={() => setDeleting(null)}
                question={`Are you sure you want to delete your ${deleting.label} file ?`}
            />}
        </div>
    );
};

export default UploadedFiles;
